import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import ReactMarkdown from "react-markdown";
import { X, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";
import { roleRoutes } from "../../utils/roles";
import {
  postChat,
  postMessage,
  connectConversationStream,
} from "../../services/aiChatService";
import { RobotIcon, AgentIcon, TrashIcon, TypingDots } from "./ChatIcons";
import { apiErrorMessage, apiErrorCode } from "../../utils/apiErrorMessage";

// ─── Degradación sin asistente ────────────────────────────────────────────────
//
// El asistente puede no estar desplegado (404), estar caído (502/503) o no
// haber red: en esos casos el chat NO está roto —el canal humano sigue vivo—
// así que en vez de un error rojo se avisa en el hilo que un agente responderá
// por ahí. Un 401/500, en cambio, sí es un error del turno y se muestra como
// tal. Sin reintentos automáticos: reintentar contra un backend caído multiplica
// la carga sin cambiar el resultado.
const ASSISTANT_UNAVAILABLE_STATUSES = [404, 502, 503];

function isAssistantUnavailable(err) {
  // Sin `.status` el fetch falló antes de tener respuesta (red/DNS/CORS).
  if (!err?.status) return true;
  return ASSISTANT_UNAVAILABLE_STATUSES.includes(err.status);
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function AIChatWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Mensajes visibles en el chat
  // Cada mensaje: { role: "user"|"assistant"|"system", content, streaming? }
  const [messages, setMessages] = useState([]);

  // Historial de mensajes para enviar a la IA (solo roles user/assistant)
  const [aiHistory, setAiHistory] = useState([]);

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusIndicator, setStatusIndicator] = useState(null); // "looking_up" | "searching" | null

  // Modo del chat: "ai" (hablando con la IA) | "human" (handoff a agente)
  const [chatMode, setChatMode] = useState("ai");
  const [conversationId, setConversationId] = useState(null);
  const [isResolved, setIsResolved] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const sseCleanupRef = useRef(null);

  const isClient = user?.role === roleRoutes.client;

  // ── Carril del FAB ───────────────────────────────────────────────────────
  // Marca el <body> mientras el botón flotante existe, para que el shell le
  // reserve espacio al final del scroll (si no, el robot flota encima del CTA
  // primario de la vista). El CSS del carril vive en _ai-chat-widget.scss.
  useEffect(() => {
    if (!isClient) return undefined;
    document.body.classList.add("has-ai-fab");
    return () => document.body.classList.remove("has-ai-fab");
  }, [isClient]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, statusIndicator]);

  // ── Foco al abrir ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [isOpen]);

  // ── Limpieza SSE al desmontar ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      sseCleanupRef.current?.();
    };
  }, []);

  // ── Conectar SSE de conversacion (post-handoff) ───────────────────────────
  const startConversationSSE = useCallback((convId) => {
    sseCleanupRef.current?.();
    sseCleanupRef.current = connectConversationStream(convId, user, {
      onMessage: (msg) => {
        if (msg.sender_role === "agent") {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: msg.content },
          ]);
        }
      },
      onAssigned: ({ message }) => {
        setMessages((prev) => [
          ...prev,
          { role: "system", content: message },
        ]);
      },
      onResolved: ({ message }) => {
        setMessages((prev) => [
          ...prev,
          { role: "system", content: message },
        ]);
        setIsResolved(true);
        sseCleanupRef.current?.();
        sseCleanupRef.current = null;
      },
    });
  }, [user]);

  // ── Handoff recibido ─────────────────────────────────────────────────────
  const handleTransfer = useCallback(({ conversation_id, message }) => {
    setMessages((prev) => [...prev, { role: "system", content: message }]);
    setConversationId(conversation_id);
    setChatMode("human");
    setIsLoading(false);
    startConversationSSE(conversation_id);
  }, [startConversationSSE]);

  // ── Enviar mensaje ───────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading || isResolved) return;

    setError(null);
    setInput("");

    const userMessage = { role: "user", content: text };

    // ── Modo humano: enviar al agente ──────────────────────────────────────
    if (chatMode === "human" && conversationId) {
      setMessages((prev) => [...prev, userMessage]);
      try {
        await postMessage(conversationId, text, user);
      } catch (err) {
        // El mensaje es optimista: el hilo lo pintó antes de que el backend lo
        // aceptara, así que al fallar se quita (por identidad, no por índice) y
        // el texto vuelve al input. `prev || text` no pisa lo que el usuario
        // haya escrito después del envío.
        setMessages((prev) => prev.filter((m) => m !== userMessage));
        setInput((prev) => prev || text);
        setError(apiErrorMessage(err, t("No se pudo enviar el mensaje.")));
      }
      return;
    }

    // ── Modo IA: streaming ────────────────────────────────────────────────
    const updatedHistory = [...aiHistory, { role: "user", content: text }];
    setAiHistory(updatedHistory);
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // Placeholder de streaming del asistente
    setMessages((prev) => [
      ...prev,
      { role: "assistant", content: "", streaming: true },
    ]);

    abortRef.current = new AbortController();

    await postChat({
      messages: updatedHistory,
      user,
      signal: abortRef.current.signal,
      onStatus: ({ status }) => {
        setStatusIndicator(status);
      },
      onChunk: (chunk) => {
        setStatusIndicator(null);
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === "assistant") {
            next[next.length - 1] = { ...last, content: last.content + chunk };
          }
          return next;
        });
      },
      onTransfer: (payload) => {
        setStatusIndicator(null);
        // Eliminar placeholder si aun esta vacio
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.streaming && !last.content) {
            return prev.slice(0, -1);
          }
          // Si la IA ya habia respondido algo, mantener la burbuja completa
          if (last?.streaming) {
            const next = [...prev];
            next[next.length - 1] = { ...last, streaming: false };
            return next;
          }
          return prev;
        });
        handleTransfer(payload);
      },
      onDone: (fullText) => {
        setStatusIndicator(null);
        setAiHistory((prev) => [
          ...prev,
          { role: "assistant", content: fullText },
        ]);
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === "assistant") {
            next[next.length - 1] = { ...last, streaming: false };
          }
          return next;
        });
        setIsLoading(false);
      },
      onError: (_msg, err) => {
        setStatusIndicator(null);
        // Se quita el placeholder de streaming en ambos caminos (quedaba una
        // burbuja vacía con los puntos animados para siempre).
        const dropStreaming = (prev) => prev.filter((m) => !m.streaming);

        // Canal del portal deshabilitado para la empresa (PORTAL_DISABLED): es
        // una decisión de configuración, no una caída — el aviso va en el hilo
        // (una sola vez) con el texto del backend, no como error rojo.
        if (apiErrorCode(err) === "PORTAL_DISABLED") {
          setMessages((prev) => {
            const next = dropStreaming(prev);
            if (next.some((m) => m.portalDisabled)) return next;
            return [
              ...next,
              {
                role: "assistant",
                portalDisabled: true,
                content: apiErrorMessage(
                  err,
                  t("El chat del portal está deshabilitado para tu empresa."),
                ),
              },
            ];
          });
          setIsLoading(false);
          return;
        }

        if (isAssistantUnavailable(err)) {
          // Degradación: el aviso va en el hilo, como un mensaje del asistente.
          // Se emite UNA sola vez por conversación (el usuario puede reintentar
          // escribiendo de nuevo; repetir el mismo aviso en cada intento solo
          // ensucia el hilo).
          setMessages((prev) => {
            const next = dropStreaming(prev);
            if (next.some((m) => m.aiUnavailable)) return next;
            return [
              ...next,
              {
                role: "assistant",
                aiUnavailable: true,
                content: t(
                  "El asistente automático no está disponible en este momento. Un agente del equipo te responderá por aquí.",
                ),
              },
            ];
          });
          // El input queda habilitado (isLoading=false) para que el usuario
          // siga escribiendo si ya está en el canal humano.
          setIsLoading(false);
          return;
        }

        setMessages(dropStreaming);
        // Nunca el `message` crudo de throwFetchApiError ('Error 403: …' o un
        // 'Forbidden' pelado): apiErrorMessage devuelve el texto del backend o,
        // si no había ninguno útil, el fallback traducido.
        setError(apiErrorMessage(err, t("No se pudo conectar con el asistente.")));
        setIsLoading(false);
      },
    });
  }, [input, isLoading, isResolved, chatMode, conversationId, aiHistory, user, handleTransfer, t]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleClearChat = useCallback(() => {
    if (isLoading) abortRef.current?.abort();
    sseCleanupRef.current?.();
    sseCleanupRef.current = null;
    setMessages([]);
    setAiHistory([]);
    setError(null);
    setIsLoading(false);
    setStatusIndicator(null);
    setChatMode("ai");
    setConversationId(null);
    setIsResolved(false);
  }, [isLoading]);

  const handleNewConversation = useCallback(() => {
    handleClearChat();
  }, [handleClearChat]);

  if (!isClient) return null;

  const inputDisabled = isLoading || isResolved;
  const showHumanBadge = chatMode === "human" && !isResolved;

  return (
    <>
      {/* ── Panel de chat ───────────────────────────────────────────────── */}
      <div
        className={`ai-chat-panel${isOpen ? " ai-chat-panel--open" : ""}${chatMode === "human" ? " ai-chat-panel--human" : ""}`}
        role="dialog"
        aria-label={t("Asistente IA (BETA)")}
        aria-modal="true"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="ai-chat-panel__header">
          <div className="ai-chat-panel__header-title">
            <span className="ai-chat-panel__robot-icon" aria-hidden="true">
              {chatMode === "human" ? <AgentIcon size={20} /> : <RobotIcon size={20} />}
            </span>
            <span>{chatMode === "human" ? t("Soporte humano") : t("Asistente IA (BETA)")}</span>
            {showHumanBadge && (
              <span className="ai-chat-panel__human-badge">{t("En vivo")}</span>
            )}
          </div>
          <div className="ai-chat-panel__header-actions">
            {messages.length > 0 && !isResolved && (
              <button
                className="ai-chat-panel__action-btn"
                onClick={handleClearChat}
                title={t("Limpiar conversación")}
                aria-label={t("Limpiar conversación")}
              >
                <TrashIcon />
              </button>
            )}
            <button
              className="ai-chat-panel__close-btn"
              onClick={handleClose}
              aria-label={t("Cerrar asistente IA")}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Mensajes */}
        <div className="ai-chat-panel__messages">
          {messages.length === 0 && (
            <div className="ai-chat-panel__empty">
              <span className="ai-chat-panel__empty-icon" aria-hidden="true">
                <RobotIcon size={40} />
              </span>
              <p>{t("¡Hola! ¿En qué puedo ayudarte hoy?")}</p>
            </div>
          )}

          {messages.map((msg, i) => {
            if (msg.role === "system") {
              return (
                <div key={i} className="ai-chat-msg ai-chat-msg--system">
                  <span>{msg.content}</span>
                </div>
              );
            }
            return (
              <div
                key={i}
                className={`ai-chat-msg ai-chat-msg--${msg.role}${msg.streaming ? " ai-chat-msg--streaming" : ""}`}
              >
                {msg.role === "assistant" && (
                  <span className="ai-chat-msg__avatar" aria-hidden="true">
                    {chatMode === "human" ? <AgentIcon size={16} /> : <RobotIcon size={16} />}
                  </span>
                )}
                <div className="ai-chat-msg__bubble">
                  {msg.content
                    ? msg.role === "assistant"
                      ? <ReactMarkdown>{msg.content}</ReactMarkdown>
                      : msg.content
                    : msg.streaming ? <TypingDots /> : null}
                </div>
              </div>
            );
          })}

          {/* Indicador de status (buscando paquete / buscando en la web) */}
          {statusIndicator && (
            <div className="ai-chat-status-indicator">
              <TypingDots />
              <span>
                {statusIndicator === "looking_up"
                  ? t("Buscando información del paquete…")
                  : t("Buscando en la web…")}
              </span>
            </div>
          )}

          {error && (
            <div className="ai-chat-panel__error" role="alert">
              {error}
            </div>
          )}

          {/* Estado resuelto */}
          {isResolved && (
            <div className="ai-chat-panel__resolved">
              <button
                className="ai-chat-panel__new-chat-btn"
                onClick={handleNewConversation}
              >
                {t("Iniciar nueva conversación")}
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="ai-chat-panel__input-area">
          <textarea
            ref={inputRef}
            className="ai-chat-panel__input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isResolved
                ? t("Conversación cerrada")
                : chatMode === "human"
                ? t("Responde al agente...")
                : t("Escribe tu mensaje...")
            }
            rows={1}
            disabled={inputDisabled}
            aria-label={t("Mensaje")}
          />
          <button
            className="ai-chat-panel__send-btn"
            onClick={handleSend}
            disabled={!input.trim() || inputDisabled}
            aria-label={t("Enviar mensaje")}
          >
            <Send size={18} />
          </button>
        </div>
      </div>

      {/* ── Overlay mobile ──────────────────────────────────────────────── */}
      {isOpen && (
        <div
          className="ai-chat-overlay"
          onClick={handleClose}
          aria-hidden="true"
        />
      )}

      {/* ── Botón flotante (FAB) ────────────────────────────────────────── */}
      <button
        className={`ai-chat-fab${isOpen ? " ai-chat-fab--active" : ""}${chatMode === "human" ? " ai-chat-fab--human" : ""}`}
        onClick={handleToggle}
        aria-label={isOpen ? t("Cerrar asistente IA") : t("Abrir asistente IA")}
        aria-expanded={isOpen}
      >
        <span className="ai-chat-fab__icon" aria-hidden="true">
          {isOpen ? (
            <X size={24} />
          ) : chatMode === "human" ? (
            <AgentIcon size={28} animated />
          ) : (
            <RobotIcon size={28} animated />
          )}
        </span>
        {!isOpen && messages.length > 0 && (
          <span className="ai-chat-fab__badge" aria-hidden="true" />
        )}
      </button>
    </>
  );
}

